import { useState, useEffect } from 'react';
import { settingsAPI } from '../api/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';

const SettingsScreen = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [reminderInterval, setReminderInterval] = useState(10);
  const [reminderIntensity, setReminderIntensity] = useState('gentle');
  const [routineTimes, setRoutineTimes] = useState({
    breakfast: { start: '07:00', end: '10:00' },
    lunch: { start: '11:00', end: '14:00' },
    dinner: { start: '17:00', end: '20:00' },
    bed: { start: '21:00', end: '23:59' },
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await settingsAPI.get();
      setSettings(data);
      setReminderInterval(data.reminder_interval);
      setReminderIntensity(data.reminder_intensity);
      setRoutineTimes(data.routine_times);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.update({
        reminder_interval: reminderInterval,
        reminder_intensity: reminderIntensity,
        routine_times: routineTimes,
      });
      toast.success('Settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('medbuddy_token');
    localStorage.removeItem('medbuddy_user');
    window.location.href = '/auth';
  };

  const handleTimeChange = (routine, field, value) => {
    setRoutineTimes({
      ...routineTimes,
      [routine]: {
        ...routineTimes[routine],
        [field]: value,
      },
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="settings-screen">
        <div className="text-2xl font-medium text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="screen-container bg-gradient-to-b from-green-50 to-white min-h-screen" data-testid="settings-screen">
      <div className="p-6 space-y-6">
        <h1 className="text-4xl font-bold text-gray-900 mb-6">Settings</h1>

        <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-gray-200 space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Reminders</h2>

            <div className="space-y-4">
              <div>
                <Label htmlFor="reminderInterval" className="text-lg font-semibold mb-2 block">
                  Repeat interval (minutes)
                </Label>
                <Select
                  value={reminderInterval.toString()}
                  onValueChange={(value) => setReminderInterval(parseInt(value))}
                >
                  <SelectTrigger data-testid="select-reminder-interval" className="h-14 text-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 minutes</SelectItem>
                    <SelectItem value="10">10 minutes</SelectItem>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="reminderIntensity" className="text-lg font-semibold mb-2 block">
                  Reminder intensity
                </Label>
                <Select
                  value={reminderIntensity}
                  onValueChange={setReminderIntensity}
                >
                  <SelectTrigger data-testid="select-reminder-intensity" className="h-14 text-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gentle">Gentle</SelectItem>
                    <SelectItem value="loud">Loud visual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-gray-200 space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Routine Times</h2>
            <p className="text-base text-gray-600 mb-4">
              Set the time windows for each routine
            </p>

            <div className="space-y-6">
              {['breakfast', 'lunch', 'dinner', 'bed'].map((routine) => (
                <div key={routine}>
                  <Label className="text-lg font-semibold mb-2 block capitalize">
                    {routine === 'bed' ? 'Before Sleep' : `After ${routine}`}
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-gray-600 mb-1 block">Start</Label>
                      <Input
                        data-testid={`input-${routine}-start`}
                        type="time"
                        value={routineTimes[routine].start}
                        onChange={(e) => handleTimeChange(routine, 'start', e.target.value)}
                        className="h-12 text-base"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600 mb-1 block">End</Label>
                      <Input
                        data-testid={`input-${routine}-end`}
                        type="time"
                        value={routineTimes[routine].end}
                        onChange={(e) => handleTimeChange(routine, 'end', e.target.value)}
                        className="h-12 text-base"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Button
          data-testid="btn-save-settings"
          onClick={handleSave}
          disabled={saving}
          className="w-full h-16 text-xl font-bold bg-green-600 hover:bg-green-700"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>

        <div className="pt-6">
          <Button
            data-testid="btn-logout"
            onClick={handleLogout}
            variant="outline"
            className="w-full h-16 text-xl font-bold text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <LogOut className="mr-3 h-6 w-6" />
            Sign Out
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SettingsScreen;